import { z } from 'zod'

import { AdminAppType } from '../constants/admin'
import {
  AdmissionGroupCancelledReason,
  AdmissionGroupStatus,
} from '../constants/admissionGroup'
import { EmailTemplate } from '../constants/email'
import { PaymentAmountType } from '../constants/pricing'
import { PubSubEvent } from '../constants/venuePubSub'
import { admissionGroupSchema } from './admissionGroup'

export const adminAppRefreshCreateBodySchema = z.object({
  appType: z.nativeEnum(AdminAppType),
})

export const adminAppRefreshResponseSchema = z.object({
  success: z.boolean(),
})

export const adminVenuePubSubEventCreateBodySchema = z
  .object({
    event: z.nativeEnum(PubSubEvent),
    link: z.string().url().optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.event === PubSubEvent.SOFT_REFRESH ||
      value.event === PubSubEvent.SOFT_REFERSH
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['event'],
        message: 'SOFT_REFRESH must be sent via /v1/admin/app-refresh',
      })
    }

    if (value.event === PubSubEvent.PRODUCT_UPDATES && !value.link) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['link'],
        message: 'link is required when event is PRODUCT_UPDATES',
      })
    }
  })

export const adminVenuePubSubEventResponseSchema = z.object({
  success: z.boolean(),
})

export const adminFavoriteVenueBodySchema = z.object({
  venueId: z.string().min(1),
})

export const adminSendTestEmailBodySchema = z.object({
  template: z.nativeEnum(EmailTemplate),
  recipientEmail: z.string().email(),
  templateData: z.record(z.string(), z.unknown()),
  subjectVars: z.record(z.string(), z.string()).optional(),
})

export const adminSendTestEmailResponseSchema = z.object({
  success: z.literal(true),
})

export const adminSeedDevDataScenarioSchema = z.object({
  enabled: z.boolean(),
  count: z.number().int().min(0).max(10),
})

export const adminSeedDevDataPaymentLinkDefaultsSchema = z.object({
  paymentAmount: z.number().min(0),
  paymentAmountType: z.nativeEnum(PaymentAmountType),
  userPaysProcessing: z.boolean(),
})

export const adminSeedDevDataBodySchema = z.object({
  venueId: z.string().min(1),
  date: z.coerce.date().optional(),
  scenarios: z.object({
    tableSingleItemBooking: adminSeedDevDataScenarioSchema,
    ticketSingleItemBooking: adminSeedDevDataScenarioSchema,
    multiItemBooking: adminSeedDevDataScenarioSchema,
    tableAdmissionGroupPaymentLink: adminSeedDevDataScenarioSchema,
    ticketAdmissionGroupPaymentLink: adminSeedDevDataScenarioSchema,
  }),
  paymentLinkDefaults: adminSeedDevDataPaymentLinkDefaultsSchema.optional(),
})

export const adminSeedDevDataResponseSchema = z.object({
  success: z.literal(true),
  created: z.object({
    bookings: z.array(z.string()),
    admissionGroups: z.array(z.string()),
    confirmationRequests: z.array(z.string()),
  }),
  errors: z.array(
    z.object({
      scenario: z.string(),
      message: z.string(),
    })
  ),
})

const admissionGroupBulkUpdateUpdatesSchema = z
  .object({
    date: z.coerce.date().optional(),
    status: z.nativeEnum(AdmissionGroupStatus).optional(),
    cancelledReasonType: z.nativeEnum(AdmissionGroupCancelledReason).optional(),
    cancelledNote: z.string().nullish(),
  })
  .superRefine((value, context) => {
    if (!value.date && !value.status) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['status'],
        message: 'date or status is required',
      })
    }

    if (
      value.status === AdmissionGroupStatus.CANCELLED &&
      !value.cancelledReasonType
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cancelledReasonType'],
        message: 'cancelledReasonType is required when status is CANCELLED',
      })
    }
  })

export const admissionGroupBulkUpdateRequestSchema = z.object({
  admissionGroupIds: z.array(z.string().min(1)).min(1).max(200),
  updates: admissionGroupBulkUpdateUpdatesSchema,
})

export const admissionGroupBulkUpdateResponseSchema = z.object({
  updated: z.array(admissionGroupSchema),
  errors: z.array(
    z.object({
      admissionGroupId: z.string(),
      code: z.string(),
      message: z.string(),
    })
  ),
})
